import React, { useCallback, useEffect } from "react";
import { useLocation, useNavigate, useRoutes } from "react-router-dom";
import { HelmetProvider, Helmet } from "react-helmet-async";
import { CacheProvider } from "@emotion/react";

import { ThemeProvider as MuiThemeProvider } from "@mui/material/styles";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { it } from "date-fns/locale";

import "./i18n";
import createTheme from "./theme";
import routes from "./routes";

import useTheme from "./hooks/useTheme";
import createEmotionCache from "./utils/createEmotionCache";

import { AuthProvider } from "./contexts/JWTContext";
import { setRole } from "./redux/slices/role";
import { useDispatch } from "react-redux";
import { rl } from "./constants";
import CustomSnackbar from "./pages/components/CustomSnackbar";
import { useSelector } from "react-redux";
import { hideSnackbar } from "./redux/slices/snackbar";

const clientSideEmotionCache = createEmotionCache();
const token = sessionStorage.getItem("token");

function App({ emotionCache = clientSideEmotionCache }) {
  //${encodeURIComponent(token)}
  const dispatch = useDispatch();
  // const messages = useSelector((state) => state.websocket.messages);
  // const status = useSelector((state) => state.websocket.status);
  // const error = useSelector((state) => state.websocket.error);
  // console.log(messages, status);
  const content = useRoutes(routes);
  const navigate = useNavigate();
  const location = useLocation();
  const isPublicRoute =
    location.pathname.startsWith("/auth") ||
    location.pathname.startsWith("/public");
  const { snackbarOpen, snackbarMessage, snackbarSeverity } = useSelector(
    (state) => state.snackbar,
  );

  const { theme } = useTheme();

  useEffect(() => {
    if (sessionStorage.getItem("token") === null && !isPublicRoute) {
      navigate("/auth/sign-in");
    }
  }, [isPublicRoute, navigate]);

  const handleClose = () => {
    dispatch(hideSnackbar());
  };

  return (
    <CacheProvider value={emotionCache}>
      <HelmetProvider>
        <Helmet
          titleTemplate="Mobility Express | %s"
          defaultTitle="Mobility Express - Dashboard"
        />
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={it}>
          <MuiThemeProvider theme={createTheme(theme)}>
            <AuthProvider>
              <CustomSnackbar
                snackbarOpen={snackbarOpen}
                snackbarMessage={snackbarMessage}
                snackbarSeverity={snackbarSeverity}
                onClose={handleClose}
              />
              {content}
            </AuthProvider>
          </MuiThemeProvider>
        </LocalizationProvider>
      </HelmetProvider>
    </CacheProvider>
  );
}

export default App;
